import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logixPanelInstance from 'i18n';
import RowsPerPageSelect from 'components/RowsPerPageSelect';
import {
  StyledPageContainer,
  StyledPageTitle
} from 'components/PageLayout/style';
import DataTable from 'components/DataTable';
import Pagination from 'components/Pagination';
import FilterAndSearchBox from 'components/FilterAndSearchBox';
import SearchBar from 'components/FilterAndSearchBox/components/SearchBar';
import FilterField from 'components/FilterAndSearchBox/components/FilterField';
import { StyledOutlineDropdownSelect } from 'components/FilterAndSearchBox/style';
import AlertToast, { AlertToastProps } from 'components/AlertToast';
import LoadingSpinner from 'components/Spinner';
import useHttp from 'hooks/useHttp';
import { usePermissionStore } from 'store';
import { PERMISSION, hasOneOfPermissions } from 'utils/permissions';
import {
  postMLAApplicationCreateAccountRequestConfig,
  putMLAApplicationStatusRequestConfig
} from 'api/getV2RequestConfig';
import useGetMLAApplicationList, {
  MLAApplicationComment,
  MLAApplicationRowData,
  MLAApplicationStatus,
  MLARole,
  MLASyncStatus
} from './hooks/useGetMLAApplicationList';
import useApplicationSubmitLock from './hooks/useApplicationSubmitLock';
import ReviewModal, { ReviewModalSaveData } from './components/ReviewModal';
import RoleTabs from './components/RoleTabs';
import TableHeadRow from './components/TableHeadRow';
import TableRow from './components/TableRow';
import { Style } from './style';
import {
  PutMLAApplicationStatusResponse,
  isPutMLAApplicationStatusError,
  isPutMLAApplicationStatusSuccess
} from './types/putMLAApplicationStatus';

const InsufficientPermissions = (): JSX.Element => {
  const { t } = useTranslation('mla', {
    i18n: logixPanelInstance
  });

  return (
    <Style.UnauthorizedContainer>
      <Style.UnauthorizedTitle>
        {t('insufficientPermissionsTitle')}
      </Style.UnauthorizedTitle>
      <Style.UnauthorizedBody>
        {t('insufficientPermissionsBody')}
      </Style.UnauthorizedBody>
      <Style.BackToHomepageLink as={Link} to="/">
        {t('backToHomepage')}
      </Style.BackToHomepageLink>
    </Style.UnauthorizedContainer>
  );
};

/**
 * act as role of review MLA applications and see the history of review data for confirmation
 */
const MLA: React.FC = () => {
  const { t, i18n } = useTranslation('mla', {
    i18n: logixPanelInstance
  });
  const { sendRequest } = useHttp();
  const isPermissionsLoaded = usePermissionStore((state) => !state.loading);
  const canAccessMLA = usePermissionStore((state) => (
    hasOneOfPermissions(state.hasPermission, [
      PERMISSION.MLA_VIEW,
      PERMISSION.MLA_EDIT
    ])
  ));
  const canEditMLA = usePermissionStore((state) => (
    state.hasPermission(PERMISSION.MLA_EDIT)
  ));

  const [currentRole, setCurrentRole] = useState<MLARole>('master');
  const [searchInput, setSearchInput] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<MLAApplicationStatus | null>(null);

  const {
    submittingApplicationIds,
    lockApplication,
    unlockApplication,
    isApplicationSubmitting
  } = useApplicationSubmitLock();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState<number>(15);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);
  const [stagingRow, setStagingRow] = useState<MLAApplicationRowData | null>(null);
  const [responseMessage, setResponseMessage] = useState<{
    type: AlertToastProps['severity'];
    content: string;
  }>({
    type: 'success',
    content: ''
  });
  const [isResponseMessageToastOpen, setIsResponseMessageToastOpen] = useState<boolean>(false);

  const {
    rows, setRows, totalPageCount, isListLoading
  } = useGetMLAApplicationList(
    currentRole,
    currentStatus,
    currentPage,
    currentRowsPerPage,
    searchInput,
    i18n.language,
    submittingApplicationIds
  );

  const statusFilterOptions = [
    {
      value: null,
      name: t('all')
    },
    {
      value: MLAApplicationStatus.Approve,
      name: t('approved')
    },
    {
      value: MLAApplicationStatus.Pending,
      name: t('pending')
    },
    {
      value: MLAApplicationStatus.Reject,
      name: t('reject')
    }
  ];

  const showToast = (
    type: AlertToastProps['severity'],
    content: string
  ): void => {
    setResponseMessage({ type, content });
    setIsResponseMessageToastOpen(true);
  };

  const openReviewModal = (row: MLAApplicationRowData): void => {
    if (!canEditMLA) return;

    setStagingRow(row);
    setIsReviewModalOpen(true);
  };

  const closeReviewModal = (): void => {
    setIsReviewModalOpen(false);
    setStagingRow(null);
  };

  const isMLAretryable = (row: MLAApplicationRowData): boolean => (
    row.status === MLAApplicationStatus.Approve &&
    (row.syncStatus === 'SYNC_FAILED' ||
    (row.syncStatus === null && row.mlaAgentTradingAccount === 0))
  );

  const createMLAAccount = (id: number): void => {
    // optimistic update
    setRows((prev) => prev.map((row) => (
      row.id === id ?
        { ...row, syncStatus: 'PENDING_SYNC' } :
        row
    )));

    sendRequest(
      postMLAApplicationCreateAccountRequestConfig(id, i18n.language),
      (responseData: {
        status_code: number;
        message: string;
        data?: {
          id: number;
          status: MLAApplicationStatus;
          sync_status: MLASyncStatus | null;
          created_lt_agent_id: number | null;
          mla_agent_trading_account: number;
        };
      }) => {
        if (typeof responseData.status_code !== 'number' || responseData.status_code < 200 || responseData.status_code >= 300 || !responseData.data) {
          showToast('error', responseData.message);
          setRows((prev) => prev.map((row) => (
            row.id === id ?
              { ...row, syncStatus: null } :
              row
          )));
          unlockApplication(id);
          return;
        }

        const { data } = responseData;
        setRows((prev) => prev.map((row) => (
          row.id === id ?
            {
              ...row,
              status: data.status,
              syncStatus: data.sync_status,
              createdLtAgentId: data.created_lt_agent_id,
              mlaAgentTradingAccount: data.mla_agent_trading_account
            } :
            row
        )));
        showToast('success', responseData.message);
        unlockApplication(id);
      },
      (errorMessage) => {
        showToast('error', errorMessage);
        setRows((prev) => prev.map((row) => (
          row.id === id ?
            { ...row, syncStatus: null } :
            row
        )));
        unlockApplication(id);
      }
    );
  };

  const rejectHandler = (data: ReviewModalSaveData): void => {
    sendRequest(
      putMLAApplicationStatusRequestConfig(
        data.row.id,
        {
          status: data.status,
          comment: data.comment
        },
        i18n.language
      ),
      (responseData: PutMLAApplicationStatusResponse) => {
        if (
          isPutMLAApplicationStatusError(responseData) ||
          !isPutMLAApplicationStatusSuccess(responseData)
        ) {
          const errorMessage = isPutMLAApplicationStatusError(responseData) ?
            responseData.message ?? t('requestFailed') :
            t('requestFailed');
          showToast('error', errorMessage);
          unlockApplication(data.row.id);
          return;
        }

        setRows((prev) => prev.map((row) => (
          row.id === responseData.id ?
            {
              ...row,
              status: responseData.status,
              comment: responseData.comment ?? MLAApplicationComment.None
            } :
            row
        )));
        showToast('success', t('reviewRejectSuccess'));
        closeReviewModal();
        unlockApplication(data.row.id);
      },
      (errorMessage) => {
        showToast('error', errorMessage);
        unlockApplication(data.row.id);
      }
    );
  };

  const approveAndCreateMLAhandler = (data: ReviewModalSaveData): void => {
    sendRequest(
      putMLAApplicationStatusRequestConfig(
        data.row.id,
        {
          status: data.status,
          comment: data.comment
        },
        i18n.language
      ),
      (responseData: PutMLAApplicationStatusResponse) => {
        if (
          isPutMLAApplicationStatusError(responseData) ||
          !isPutMLAApplicationStatusSuccess(responseData)
        ) {
          const errorMessage = isPutMLAApplicationStatusError(responseData) ?
            responseData.message ?? t('requestFailed') :
            t('requestFailed');
          showToast('error', errorMessage);
          unlockApplication(data.row.id);
          return;
        }

        const approvedRow: MLAApplicationRowData = {
          ...data.row,
          status: responseData.status ?? MLAApplicationStatus.Approve,
          comment: responseData.comment ?? MLAApplicationComment.None
        };

        setRows((prev) => prev.map((row) => (
          row.id === data.row.id ? approvedRow : row
        )));
        closeReviewModal();
        createMLAAccount(approvedRow.id);
      },
      (errorMessage) => {
        showToast('error', errorMessage);
        unlockApplication(data.row.id);
      }
    );
  };

  const handleRetry = (row: MLAApplicationRowData): void => {
    if (!canEditMLA || !lockApplication(row.id)) return;

    if (!isMLAretryable(row)) {
      showToast('error', t('createAccountNotAllowed'));
      unlockApplication(row.id);
      return;
    }

    createMLAAccount(row.id);
  };

  const handleSaveReview = (data: ReviewModalSaveData): void => {
    if (!canEditMLA || !lockApplication(data.row.id)) return;

    if (data.status === MLAApplicationStatus.Approve) {
      approveAndCreateMLAhandler(data);
      return;
    }

    if (data.status === MLAApplicationStatus.Reject) {
      rejectHandler(data);
      return;
    }

    unlockApplication(data.row.id);
  };

  const isStagingRowSubmitting = stagingRow !== null &&
    isApplicationSubmitting(stagingRow.id);

  const tableRows = useMemo(
    () => rows.map((row) => (
      <TableRow
        key={row.id}
        rowData={row}
        canEdit={canEditMLA}
        isRowSubmitting={isApplicationSubmitting(row.id)}
        onReview={openReviewModal}
        onRetry={handleRetry}
      />
    )),
    [rows, canEditMLA, submittingApplicationIds]
  );

  if (!isPermissionsLoaded) {
    return (
      <Style.SpinnerContainer>
        <LoadingSpinner />
      </Style.SpinnerContainer>
    );
  }

  if (!canAccessMLA) return <InsufficientPermissions />;

  return (
    <StyledPageContainer>
      <StyledPageTitle>{t('pageTitle')}</StyledPageTitle>
      <RoleTabs
        activeRole={currentRole}
        onRoleChange={(role) => {
          setCurrentRole(role);
          setCurrentPage(1);
        }}
      />
      <FilterAndSearchBox
        filterSlot={(
          <FilterField label={t('status')}>
            <StyledOutlineDropdownSelect
              selectedValue={currentStatus}
              options={statusFilterOptions}
              onSelect={(value) => {
                setCurrentStatus(value);
                setCurrentPage(1);
              }}
              width="247px"
            />
          </FilterField>
        )}
        searchSlot={(
          <SearchBar
            placeholder={t('searchPlaceholder')}
            onSearch={(value) => {
              setSearchInput(value);
              setCurrentPage(1);
            }}
          />
        )}
      />
      <Style.TableLoadingContainer aria-busy={isListLoading}>
        {isListLoading && (
        <Style.TableLoadingOverlay aria-label="Loading table data">
          <LoadingSpinner type="MoonLoader" />
        </Style.TableLoadingOverlay>
        )}
        <DataTable
          tableHeadSlot={<TableHeadRow />}
          tableBodySlot={tableRows}
          tableFooterSlot={(
            <>
              <Pagination
                total={totalPageCount}
                page={currentPage}
                onPageChange={(value) => setCurrentPage(value)}
              />
              <RowsPerPageSelect
                count={currentRowsPerPage}
                onSelect={(value) => {
                  setCurrentRowsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </>
          )}
        />
      </Style.TableLoadingContainer>
      <ReviewModal
        isOpen={isReviewModalOpen}
        isFormSubmitting={isStagingRowSubmitting}
        rowData={stagingRow}
        canEdit={canEditMLA}
        onClose={closeReviewModal}
        onSave={handleSaveReview}
      />
      <AlertToast
        isOpen={isResponseMessageToastOpen}
        handleClose={() => setIsResponseMessageToastOpen(false)}
        severity={responseMessage.type}
        content={responseMessage.content}
      />
    </StyledPageContainer>
  );
};

export default MLA;
